import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { paymentService } from '../apis/paymentService';
import { useTrial } from '../context/TrialContext';
import { Header } from '../components/header';
import PaymentReceiptDocument from '../components/billing/PaymentReceiptDocument';
import ReceiptShareModal from '../components/billing/ReceiptShareModal';
import { printReceiptDocument, downloadReceiptHtml, downloadReceiptPdf } from '../utils/receiptExport';
import {
  navigateToScribeAfterPayment,
  prepareSubscriptionAfterPayment,
} from '../utils/postPaymentNavigation';
import { toast } from 'react-hot-toast';
import '../components/billing/payment-receipt.css';
import { fireMntnConversion } from '../components/mntn-analytics/mntnPixel';

const REDIRECT_SECONDS = 60;

function extractPaymentFromResponse(response) {
  const data = response?.data ?? response;
  return (
    data?.payment ??
    data?.body?.data?.payment ??
    data?.data?.payment ??
    null
  );
}

function isPaymentCompleted(payment) {
  const status = String(payment?.status || '').toLowerCase();
  return status === 'completed' || status === 'paid' || status === 'settled';
}

const PaymentReceipt = () => {
  const { paymentId } = useParams();
  const [payment, setPayment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [redirectSeconds, setRedirectSeconds] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const { refreshTrialStatus } = useTrial();

  useEffect(() => {
    const fetchPayment = async () => {
      try {
        const response = await paymentService.getPaymentById(paymentId);
        const paymentData = extractPaymentFromResponse(response);
        if (!paymentData) {
          setError('Payment not found');
          return;
        }
        setPayment(paymentData);
      } catch (err) {
        setError('Failed to load payment details');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    if (paymentId) {
      fetchPayment();
    }
  }, [paymentId]);

  useEffect(() => {
    if (!payment || !isPaymentCompleted(payment)) {
      return undefined;
    }
    // MNTN Conversion Pixel
    fireMntnConversion({
      orderId: payment.id || paymentId,
      orderAmount: payment.amount ?? payment.amount_paid ?? payment.total ?? '',
    });

    let cancelled = false;
    void (async () => {
      await prepareSubscriptionAfterPayment(paymentId);
      if (!cancelled) {
        refreshTrialStatus();
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [payment, paymentId, refreshTrialStatus]);

  useEffect(() => {
    if (!payment || !isPaymentCompleted(payment)) {
      return undefined;
    }

    const deadline = Date.now() + REDIRECT_SECONDS * 1000;
    let redirected = false;

    const goToScribe = () => {
      if (redirected) return;
      redirected = true;
      setRedirectSeconds(0);
      navigateToScribeAfterPayment();
    };

    setRedirectSeconds(REDIRECT_SECONDS);

    const tick = () => {
      const left = Math.max(0, Math.ceil((deadline - Date.now()) / 1000));
      setRedirectSeconds(left);
      if (left <= 0) {
        goToScribe();
      }
    };

    tick();
    const interval = window.setInterval(tick, 200);
    const timeout = window.setTimeout(goToScribe, REDIRECT_SECONDS * 1000);

    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [payment]);

  const receiptFilename = payment
    ? `vps-invoice-${String(payment.id || paymentId).slice(0, 8)}.html`
    : 'vps-invoice.html';

  const pdfFilename = payment
    ? `vps-invoice-${String(payment.id || paymentId).slice(0, 8)}.pdf`
    : 'vps-invoice.pdf';

  const handlePrint = useCallback(() => {
    printReceiptDocument();
  }, []);

  const handleDownload = useCallback(() => {
    const ok = downloadReceiptHtml('.vps-receipt-print-root', receiptFilename);
    if (ok) {
      toast.success('Invoice HTML downloaded');
    } else {
      toast.error('Could not download invoice');
    }
  }, [receiptFilename]);

  const handleDownloadPdf = useCallback(() => {
    if (!payment) return;
    try {
      const ok = downloadReceiptPdf(payment, pdfFilename);
      if (ok) {
        toast.success('Invoice PDF downloaded');
      } else {
        toast.error('Could not download PDF');
      }
    } catch (err) {
      console.error(err);
      toast.error('Could not download PDF');
    }
  }, [payment, pdfFilename]);

  const handleGoToScribe = useCallback(() => {
    navigateToScribeAfterPayment();
  }, []);

  const receiptUrl = `${window.location.origin}/payment-receipt/${paymentId}`;

  const handleCopyReceiptLink = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(receiptUrl);
      toast.success('Receipt link copied');
    } catch {
      toast.error('Could not copy link');
    }
  }, [receiptUrl]);

  const handleShareReceipt = useCallback(async ({ email, message }) => {
    try {
      const response = await paymentService.shareReceipt(paymentId, { email, message });
      const payload = response?.data ?? response;
      if (payload?.success === false) {
        throw new Error(payload?.error || 'Failed to share receipt');
      }
      toast.success(`Receipt sent to ${email}`);
    } catch (err) {
      const errorMessage =
        err?.response?.data?.error ||
        err?.message ||
        'Failed to share receipt';
      toast.error(errorMessage);
      throw err;
    }
  }, [paymentId]);

  const handleOpenShare = useCallback(() => {
    setShareOpen(true);
  }, []);

  const showRedirectBanner =
    payment &&
    isPaymentCompleted(payment) &&
    redirectSeconds != null &&
    redirectSeconds > 0;

  return (
    <>
      <div className="vps-receipt-no-print">
        <Header />
      </div>
      <div className="pay-page-shell pay-page-shell--receipt">
        <div className="pay-page-inner">
          {loading ? (
            <div className="flex items-center justify-center flex-1 min-h-[12rem]">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-emerald-600" />
            </div>
          ) : error || !payment ? (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-2xl w-full">
              {error || 'Payment not found'}
            </div>
          ) : (
            <>
              {showRedirectBanner && (
                <div
                  className="vps-receipt-redirect-banner vps-receipt-no-print"
                  role="status"
                  aria-live="polite"
                  aria-label={`Redirecting to VirtualScribe in ${redirectSeconds} seconds`}
                >
                  <p className="vps-receipt-redirect-text">
                    Redirecting to VirtualScribe in {redirectSeconds} second{redirectSeconds === 1 ? '' : 's'}…
                  </p>
                  <div className="vps-receipt-redirect-track" aria-hidden="true">
                    <div
                      className="vps-receipt-redirect-bar"
                      style={{ animationDuration: `${REDIRECT_SECONDS}s` }}
                    />
                  </div>
                </div>
              )}
              <PaymentReceiptDocument
                payment={payment}
                onPrint={handlePrint}
                onDownload={handleDownload}
                onDownloadPdf={handleDownloadPdf}
                onShare={isPaymentCompleted(payment) ? handleOpenShare : undefined}
                onBack={handleGoToScribe}
                onSecondary={() => {
                  window.location.assign('/payment');
                }}
                backLabel="Go to VirtualScribe now"
                secondaryLabel="Subscribe again"
              />
              <ReceiptShareModal
                isOpen={shareOpen}
                onClose={() => setShareOpen(false)}
                payment={payment}
                onShareEmail={handleShareReceipt}
                onCopyLink={handleCopyReceiptLink}
              />
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default PaymentReceipt;
